import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Zap, ShieldCheck, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = { standard: Zap, pro: Crown };

export default function Pricing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const { user, refresh } = useAuth();

  const load = () => api.getPlans().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const res = await api.subscribe(plan);
      toast.message("Demande enregistrée", { description: res.message, duration: 8000 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy("");
    }
  };

  const current = data?.current_plan || user?.plan || "free";

  return (
    <div className="px-4 md:px-8 py-10 max-w-[1100px] mx-auto space-y-8 animate-fade-in" data-testid="pricing-page">
      <div className="text-center">
        <div className="font-mono text-xs text-primary tracking-widest mb-2">// FORMULES</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Choisis ta formule</h1>
        <p className="text-muted-foreground mt-2 text-sm">Formule actuelle : <span className="font-mono text-primary uppercase">{current}</span></p>
        {user?.is_trial && user?.trial_ends_at && (
          <p className="mt-2 text-xs font-mono text-warning" data-testid="trial-banner">
            Essai Pro — expire le {new Date(user.trial_ends_at).toLocaleDateString()}. Abonne-toi pour conserver l'accès Pro.
          </p>
        )}
      </div>

      {data && !data.paypal_configured && (
        <div className="flex items-start gap-3 bg-warning/10 border border-warning/30 rounded-md p-4" data-testid="paypal-warning">
          <Info className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="text-warning font-semibold">Mode manuel —</span> le paiement PayPal automatique n'est pas encore connecté.
            En cliquant sur « S'abonner », ta demande est enregistrée et un administrateur activera ton accès après réception du paiement.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {(data?.plans || []).map(p => {
          const Icon = ICONS[p.id] || Zap;
          const isCurrent = current === p.id;
          const isPro = p.id === "pro";
          return (
            <Card key={p.id} data-testid={`plan-${p.id}`} className={cn(
              "relative p-7 flex flex-col",
              isPro ? "bg-card/70 border-primary/50 shadow-red-glow" : "bg-card/60 border-border"
            )}>
              {isPro && <Badge className="absolute top-5 right-5 bg-primary text-primary-foreground font-mono text-[10px]">POPULAIRE</Badge>}
              <div className="flex items-center gap-3 mb-4">
                <div className={cn("h-10 w-10 rounded-md flex items-center justify-center border", isPro ? "bg-primary/15 border-primary/40" : "bg-muted/30 border-border")}>
                  <Icon className={cn("h-5 w-5", isPro ? "text-primary" : "text-foreground")} />
                </div>
                <div>
                  <div className="font-bold text-lg">{p.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">offensive recon suite</div>
                </div>
              </div>
              <div className="flex items-end gap-1 mb-5">
                <span className="text-4xl font-bold font-mono">${p.price}</span>
                <span className="text-muted-foreground text-sm mb-1">/ {p.period}</span>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-success mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`subscribe-${p.id}-btn`}
                onClick={() => subscribe(p.id)}
                disabled={isCurrent || busy === p.id}
                className={cn("w-full font-mono tracking-widest gap-2",
                  isPro ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "")}
                variant={isPro ? "default" : "outline"}
              >
                {isCurrent ? <><ShieldCheck className="h-4 w-4" /> FORMULE ACTUELLE</> : busy === p.id ? "..." : `S'ABONNER — $${p.price}`}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
